<template>
  <header class="sticky top-0 z-50 border-b border-[#d8c2ab] bg-[#f8efe2]/95 backdrop-blur-xl">
    <div class="border-b border-[#dfcbb7] bg-[#9f1715] py-2 overflow-hidden">
      <div class="whitespace-nowrap text-[#f8f1e7] text-[10px] font-black tracking-[0.35em] uppercase animate-marquee">
        {{ tags.tagsText }}
      </div>
    </div>

    <div class="max-w-[1700px] mx-auto px-4 md:px-8 h-[92px] flex justify-between md:justify-start items-center gap-6">
      <div class="w-12 md:w-auto flex items-center md:hidden">
        <button class="text-[#7f140f]" @click="toggleMenu()">
          <i class="pi pi-bars text-2xl"></i>
        </button>
      </div>

      <router-link to="/" class="shrink-0 flex items-center mx-auto md:mx-0">
        <div class="font-anton uppercase leading-none text-[#981515] text-4xl tracking-tight">
          <LogoComponent :theme="'light'" :customClass="'text-2xl md:text-4xl'" />
        </div>
      </router-link>

      <nav class="hidden lg:flex items-center gap-8 ml-10" v-if="isBlogPrefix">
        <router-link to="/"
          class="text-sm uppercase tracking-[0.18em] font-bold text-[#6a3328] hover:text-[#ea6b1d] transition-all">
          {{ translate('home', 'ucfirst') }}
        </router-link>
        <router-link to="/blog/home"
          class="text-sm uppercase tracking-[0.18em] font-bold text-[#6a3328] hover:text-[#ea6b1d] transition-all">
          {{ translate('blog', 'ucfirst') }}
        </router-link>
        <router-link :to="BlogPostService.getAboutBlogPostInfo()"
          class="text-sm uppercase tracking-[0.18em] font-bold text-[#6a3328] hover:text-[#ea6b1d] transition-all">
          {{ translate('about', 'ucfirst') }}
        </router-link>
      </nav>

      <div class="hidden md:flex flex-1 max-w-4xl ml-auto relative">
        <AutoComplete v-model="selectedCourse" :suggestions="courseSuggestions" optionLabel="label"
          :emptySearchMessage="translate('no course found', 'ucfirst')"
          @change="checkSelectedCourse()" @complete="search" 
          :placeholder="translate('what course do you seek?', 'ucfirst')"
          class="w-full" 
          inputClass="w-full rounded-full border border-[#d9c1aa] bg-[#fffaf4] py-3 pl-14 pr-5 text-sm text-[#5d372b] focus:border-[#ea6b1d] focus:ring-0 shadow-none"
        >
          <template #option="slotProps">
            <div class="flex items-center gap-3 py-1">
              <img 
                :src="slotProps.option.image || 'https://placehold.co/56x32'" 
                alt="Course Image" 
                class="w-14 aspect-video object-cover rounded-md border border-[#d9c1aa]"
              />
              <span class="text-sm font-medium text-[#5d372b]">{{ slotProps.option.label }}</span>
            </div>
          </template>
        </AutoComplete>
        <i class="pi pi-search relative my-auto text-[#b47b52]" style="margin-left: -1.5rem"></i>
      </div>

      <div class="flex items-center justify-end gap-2 w-12 md:w-auto md:ml-auto">
        <button class="md:hidden h-11 w-11 rounded-full bg-[#f3e5d4] text-[#7d140f] flex items-center justify-center" @click="openSearcherDialog()">
          <i class="pi pi-search"></i>
        </button>

        <div class="hidden md:block">
          <UserMenuComponent />
        </div>
      </div>
    </div>

    <MobilePublicMenu :show="mobileMenu" @closed="mobileMenu = false" />

    <Dialog 
      v-model:visible="isSearchOpen" 
      modal 
      position="top"
      :header="translate('search', 'ucfirst')" 
      :draggable="false"
      :dismissableMask="true"
      class="w-full max-w-lg mx-4 bg-[#fffaf4] rounded-2xl"
      :pt="{
        root: { class: 'border-0 shadow-xl' },
        header: { class: 'bg-[#fffaf4] pt-6 px-6 text-[#5d372b] font-bold font-anton' },
        content: { class: 'bg-[#fffaf4] pb-8 px-6' }
      }"
    >
      <div class="relative w-full mt-2">
        <AutoComplete v-model="selectedCourse" :suggestions="courseSuggestions" optionLabel="label"
          :emptySearchMessage="translate('no course found', 'ucfirst')"
          @change="handleMobileSearch" @complete="search" :placeholder="translate('what course do you seek?', 'ucfirst')"
          class="w-full" 
          inputClass="w-full rounded-full border border-[#d9c1aa] bg-[#fffaf4] py-4 pl-14 pr-5 text-base text-[#5d372b] focus:border-[#ea6b1d] focus:ring-0 shadow-none"
          autofocus
        >
          <template #option="slotProps">
            <div class="flex items-center gap-4 py-2">
              <img 
                :src="slotProps.option.image || 'https://placehold.co/64x36'" 
                alt="Course Image" 
                class="w-16 aspect-video object-cover rounded-md border border-[#d9c1aa]"
              />
              <span class="text-base font-medium text-[#5d372b]">{{ slotProps.option.label }}</span>
            </div>
          </template>
        </AutoComplete>
      </div>
    </Dialog>
  </header>
</template>

<script setup>
import LogoComponent from '../LogoComponent.vue';
import { onMounted, reactive, ref, defineProps, watch } from 'vue';
import { sleep } from '@/composables/sleep';
import BlogPostService from '@/services/BlogPostService';
import UserMenuComponent from '../Header/UserMenuComponent.vue';
import MobilePublicMenu from '../menus/MobilePublicMenu.vue';

import Dialog from 'primevue/dialog';
import AutoComplete from 'primevue/autocomplete';
import CourseService from '@/services/CourseService';
import urlHandler from '@/composables/urlHandler';
import utils from '@/composables/utils';
import { translate } from '@/composables/translate';

const props = defineProps({
  isBlogPrefix: {
    type: Boolean,
    default: false,
  }
});

const isBlogPrefix = ref(false);

watch(
  () => props.isBlogPrefix,
  (newValue) => {
    isBlogPrefix.value = newValue;
  },
  { immediate: true }
);

const isSearchOpen = ref(false);
const selectedCourse = ref(null);
const courseSuggestions = ref([]);
const loadingCoursesSearch = ref(false);

onMounted(() => {
  scrollTop();
  getTags();
});

// Scroll window smoothly to the top
async function scrollTop() {
  for (let i = 0; i < 5; i++) {
    await sleep(100);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
}

// Banner Marquee Tags logic
const tags = reactive({
  items: [],
  tags: [],
  tagsText: ''
});

async function getTags() {
  const response = await BlogPostService.getTags();
  tags.items = [];
  tags.tags = [];
  tags.tagsText = '';

  if (response) {
    tags.items = response.items;
    tags.tags = response.tags;

    let tagsToExplode = response.tags.length < 2 ? response.tags.concat(response.tags) : response.tags;

    if (tagsToExplode.length < 10) {
      let thisTags = [];
      for (let i = 0; i < 10; i++) {
        thisTags = thisTags.concat(tagsToExplode);
      }

      tagsToExplode = thisTags;
    }

    tags.tagsText = tagsToExplode.join(' • ');
  }
}

// Side Mobile Menu Controls
const mobileMenu = ref(false);
function toggleMenu() {
  mobileMenu.value = !mobileMenu.value;
}

// Mobile Search Modal Actions
function openSearcherDialog() {
  selectedCourse.value = null;
  isSearchOpen.value = true;
}

function handleMobileSearch() {
  if (checkSelectedCourse()) {
    isSearchOpen.value = false;
  }
}

// Search Business Logic Core
async function search(event) {
  await sleep(300);

  let term = event != undefined 
      ? event.query 
      : mobileSearch.value;
  
  if (utils.isEmpty(term) || term.length < '3') {
      return;
  }

  loadingCoursesSearch.value = true;

  let coursesList = [];
  const response = await CourseService.searchCourseWithFilters({title: term, limit: 10});
  
  for (let course of response) {
    coursesList.push({
      label: course.title,
      code: course.uuid,
      image: course.getCoursePhoto()
    });

  }

  courseSuggestions.value = coursesList;
  loadingCoursesSearch.value = false;
}

function checkSelectedCourse() {
    if (selectedCourse.value.code != undefined) {
      isSearchOpen.value = false;
      redirectToCourse(selectedCourse.value.code);
      return true;
    }
    return false;
}

const mobileSearch = ref();
function redirectToCourse(uuid) {
    if (!urlHandler.currentRouteContains(`/course/${uuid}`)) {
        // showSearcherDialog.value = false;
        urlHandler.redirectAndScroll(`/course/${uuid}`);
    }
}

</script>

<style scoped>
@keyframes marquee {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

.animate-marquee {
  animation: marquee 18s linear infinite;
}
</style>